import { useCallback, useRef, useEffect } from 'react';

type SoundType = 'error' | 'success' | 'warning' | 'click';

// Web Audio API synthesized sounds for accessibility
const createAudioContext = () => {
  if (typeof window !== 'undefined' && (window.AudioContext || (window as any).webkitAudioContext)) {
    return new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  return null;
};

export const useSoundEffects = () => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const enabledRef = useRef(true);

  // Initialize audio context lazily (requires user interaction)
  const getContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = createAudioContext();
    }
    return audioContextRef.current;
  }, []);

  // Play error sound - descending two-tone
  const playErrorSound = useCallback(() => {
    const ctx = getContext();
    if (!ctx || !enabledRef.current) return;

    // Resume context if suspended (browser autoplay policy)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const now = ctx.currentTime;
    
    // Create oscillator for error sound (descending tone)
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(440, now); // A4
    osc.frequency.setValueAtTime(330, now + 0.1); // E4 (descending)
    
    gain.gain.setValueAtTime(0.15, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
    
    osc.start(now);
    osc.stop(now + 0.25);
  }, [getContext]);

  // Play success sound - ascending two-tone chime
  const playSuccessSound = useCallback(() => {
    const ctx = getContext();
    if (!ctx || !enabledRef.current) return;

    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const now = ctx.currentTime;
    
    // Create oscillator for success sound (ascending chime)
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc1.connect(gain);
    osc2.connect(gain);
    gain.connect(ctx.destination);
    
    // First note - C5
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(523, now);
    
    // Second note - E5 (delayed)
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(659, now + 0.1);
    
    gain.gain.setValueAtTime(0.12, now);
    gain.gain.setValueAtTime(0.15, now + 0.1);
    gain.gain.setValueAtTime(0.01, now + 0.4);
    
    osc1.start(now);
    osc1.stop(now + 0.15);
    osc2.start(now + 0.1);
    osc2.stop(now + 0.4);
  }, [getContext]);

  // Play warning sound - single mid tone
  const playWarningSound = useCallback(() => {
    const ctx = getContext();
    if (!ctx || !enabledRef.current) return;

    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const now = ctx.currentTime;
    
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(392, now); // G4
    
    gain.gain.setValueAtTime(0.1, now);
    gain.gain.setValueAtTime(0.01, now + 0.2);
    
    osc.start(now);
    osc.stop(now + 0.2);
  }, [getContext]);

  // Play click sound - very short tick
  const playClickSound = useCallback(() => {
    const ctx = getContext();
    if (!ctx || !enabledRef.current) return;

    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const now = ctx.currentTime;
    
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(1000, now);
    
    gain.gain.setValueAtTime(0.05, now);
    gain.gain.setValueAtTime(0.01, now + 0.03);
    
    osc.start(now);
    osc.stop(now + 0.03);
  }, [getContext]);

  // Main play function
  const playSound = useCallback((type: SoundType) => {
    switch (type) {
      case 'error':
        playErrorSound();
        break;
      case 'success':
        playSuccessSound();
        break;
      case 'warning':
        playWarningSound();
        break;
      case 'click':
        playClickSound();
        break;
    }
  }, [playErrorSound, playSuccessSound, playWarningSound, playClickSound]);

  // Toggle sound effects
  const setEnabled = useCallback((enabled: boolean) => {
    enabledRef.current = enabled;
  }, []);

  // Check if sounds are supported
  const isSupported = typeof window !== 'undefined' && 
    (!!window.AudioContext || !!(window as any).webkitAudioContext);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, []);

  return {
    playSound,
    playErrorSound,
    playSuccessSound,
    playWarningSound,
    playClickSound,
    setEnabled,
    isSupported,
  };
};
